import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  LayoutDashboard, Users, Calendar, FileText, 
  DollarSign, TrendingUp, BarChart3, FolderOpen, LogOut, Globe
} from 'lucide-react';

const Sidebar = ({ onLinkClick }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'hr_manager', 'dept_manager', 'employee'] },
    { path: '/employees', icon: Users, label: 'Employees', roles: ['admin', 'hr_manager'] },
    { path: '/attendance', icon: Calendar, label: 'Attendance', roles: ['admin', 'hr_manager', 'dept_manager'] },
    { path: '/leave', icon: FileText, label: 'Leave', roles: ['admin', 'hr_manager', 'dept_manager', 'employee'] },
    { path: '/payroll', icon: DollarSign, label: 'Payroll', roles: ['admin', 'hr_manager'] },
    { path: '/incentives', icon: TrendingUp, label: 'Incentives', roles: ['admin', 'hr_manager'] },
    { path: '/kpi', icon: BarChart3, label: 'KRA/KPI', roles: ['admin', 'hr_manager', 'dept_manager', 'employee'] },
    { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['admin', 'hr_manager'] },
    { path: '/documents', icon: FolderOpen, label: 'Documents', roles: ['admin', 'hr_manager', 'employee'] },
    { path: '/users', icon: Users, label: 'User Management', roles: ['admin', 'hr_manager'] },
  ];

  const filteredMenu = menuItems.filter(item => item.roles.includes(user?.role));

  // Theme-based styles
  const styles = {
    header: isDarkMode ? 'border-b border-[hsl(215_10%_22%)]' : 'border-b border-gray-200',
    logo: 'bg-gradient-to-br from-[#00B8B8] to-[#0EA5E9]',
    title: isDarkMode ? 'text-white' : 'text-gray-900',
    subtitle: 'text-[#0EA5E9]',
    userName: isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900',
    userRole: isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500',
    headerLine: isDarkMode ? 'border-t border-[hsl(215_10%_22%)]' : 'border-t border-gray-200',
    menuItem: isDarkMode ? 'text-[hsl(215_10%_70%)] hover:bg-[hsl(215_8%_20%)] hover:text-[hsl(210_15%_92%)]' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
    menuItemActive: 'bg-[#00B8B8] text-white shadow-[0_0_20px_rgba(0,184,184,0.3)]',
    logoutBtn: isDarkMode ? 'text-[hsl(215_10%_70%)] hover:bg-[#3B1E21] hover:text-[#FFB3B8]' : 'text-gray-600 hover:bg-red-50 hover:text-red-600',
  };

  return (
    <div className="h-full flex flex-col">
      <div className={`p-6 ${styles.header}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
            <img src="/danwei-logo.png" alt="Danwei HR" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className={`text-lg font-bold ${styles.title} tracking-tight`}>Danwei HR</h1>
            <p className={`text-xs ${styles.subtitle}`}>Human Resource Suite</p>
          </div>
        </div>
        <div className={`mt-4 pt-4 ${styles.headerLine}`}>
          <p className={`text-sm ${styles.userName} font-medium`}>{user?.full_name}</p>
          <p className={`text-xs ${styles.userRole} capitalize`}>{user?.role?.replace('_', ' ')}</p>
        </div>
      </div>

      <nav className="p-4 flex-1">
        {filteredMenu.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onLinkClick}
              data-testid={`nav-${item.label.toLowerCase().replace(/\/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors duration-150 ${
                isActive ? styles.menuItemActive : styles.menuItem
              }`}
            >
              <Icon size={20} />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4">
        <button
          onClick={logout}
          data-testid="logout-button"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg w-full ${styles.logoutBtn} transition-colors duration-150`}
        >
          <LogOut size={20} />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
